import * as go from 'gojs';
import { getImageSplit } from 'unitData/Split/getImageSplit';
import { Template } from '../../utils/addAndSplitTemplate';
import theme from 'theme/theme';
import type { AddSplitBaseNodeData, AvailablePort, DPorts } from 'gojs/types';

const $ = go.GraphObject.make;

const alignSplitPorts = (portIndex: number, angle: number) => {
  if (angle === 90) {
    switch (portIndex) {
      case 0:
        return new go.Spot(go.Spot.Right.x, go.Spot.Right.y, -0.5, -0.25);
      case 1:
        return new go.Spot(go.Spot.TopLeft.x, go.Spot.TopLeft.y, 0.25, 0.25);
      case 2:
        return new go.Spot(
          go.Spot.BottomLeft.x,
          go.Spot.BottomLeft.y,
          0.25,
          -0.75,
        );
      case 3:
        return new go.Spot(go.Spot.Left.x, go.Spot.Left.y, 0.25, 0);
    }
  } else if (angle === 180) {
    switch (portIndex) {
      case 0:
        return new go.Spot(go.Spot.Bottom.x, go.Spot.Bottom.y, 0, -0.25);
      case 1:
        return new go.Spot(go.Spot.TopRight.x, go.Spot.TopRight.y, -0.75, 0.25);
      case 2:
        return new go.Spot(go.Spot.TopLeft.x, go.Spot.TopLeft.y, 0.25, 0.25);
      case 3:
        return new go.Spot(go.Spot.Top.x, go.Spot.Top.y, 0, 0.25);
    }
  } else if (angle === 270) {
    switch (portIndex) {
      case 0:
        return new go.Spot(go.Spot.Left.x, go.Spot.Left.y, 0, -0.25);
      case 1:
        return new go.Spot(
          go.Spot.BottomRight.x,
          go.Spot.BottomRight.y,
          -0.75,
          -0.75,
        );
      case 2:
        return new go.Spot(go.Spot.TopRight.x, go.Spot.TopRight.y, -0.75, 0.25);
      case 3:
        return new go.Spot(go.Spot.Right.x, go.Spot.Right.y, -0.75, 0);
    }
  } else {
    switch (portIndex) {
      case 0:
        return new go.Spot(go.Spot.Top.x, go.Spot.Top.y, 0, 0);
      case 1:
        return new go.Spot(
          go.Spot.BottomLeft.x,
          go.Spot.BottomLeft.y,
          0.25,
          -0.75,
        );
      case 2:
        return new go.Spot(
          go.Spot.BottomRight.x,
          go.Spot.BottomRight.y,
          -0.5,
          -0.75,
        );
      case 3:
        return new go.Spot(go.Spot.Bottom.x, go.Spot.Bottom.y, 0, -0.25);
    }
  }
};

const Input = () => [
  $(
    go.Panel,
    'Auto',
    new go.Binding('itemArray', 'D0'),
    new go.Binding('alignment', '', (node: AddSplitBaseNodeData) =>
      alignSplitPorts(0, node.angle),
    ),
    {
      itemTemplate: $(
        go.Panel,
        $(
          go.Panel,
          'Auto',
          {
            toLinkable: true,
            toMaxLinks: 1,
            toEndSegmentLength: 0,
          },
          new go.Binding('toSpot', '', (data: AddSplitBaseNodeData, node) => {
            return node.panel.panel.alignment;
          }),
          new go.Binding('portId', 'key'),
        ),
      ),
    },
    $(
      go.Panel,
      'Spot',
      new go.Binding('alignment', '', (data: AddSplitBaseNodeData) =>
        alignSplitPorts(0, data.angle),
      ),
      $(
        go.Shape,
        'Circle',
        {
          width: 6,
          height: 6,
          fill: theme.palette.info.main,
          stroke: null,
        },
        new go.Binding('opacity', '', (data: AddSplitBaseNodeData) => {
          return data['D0' as keyof DPorts]?.[0].connected ? 0 : 1;
        }),
      ),
    ),
  ),
];

const Output = (index: number, port: keyof DPorts) =>
  $(
    go.Panel,
    'Spot',
    new go.Binding('itemArray', `D${index}`),
    new go.Binding('alignment', '', (data: AddSplitBaseNodeData) =>
      alignSplitPorts(index, data.angle),
    ),
    new go.Binding('visible', '', (data: AddSplitBaseNodeData) => {
      if (index < 3) {
        return true;
      }
      return data.nbrOfOutputs >= 3;
    }),
    {
      itemTemplate: $(
        go.Panel,
        'Spot',
        {
          fromLinkable: true,
          fromMaxLinks: 1,
          cursor: 'pointer',
        },
        new go.Binding('angle', '', (data: AvailablePort, node) => {
          let newAngle = 0;
          if (index === 1) {
            newAngle = 45;
          } else if (index === 2) {
            newAngle = 360 - 45;
          }
          return newAngle + node.part.data.angle;
        }),
        new go.Binding('portId', 'key'),
        $(
          go.Shape,
          'TriangleDown',
          {
            stroke: null,
            fill: theme.palette.info.main,
          },
          new go.Binding('desiredSize', '', (data: AvailablePort, node) => {
            return node.part.data[port]?.[0].connected
              ? new go.Size(6, 6)
              : new go.Size(12, 12);
          }),
        ),
      ),
    },
  );

const SplitTemplate = Template(
  [...Input(), Output(1, 'D1'), Output(2, 'D2'), Output(3, 'D3')],
  (node: AddSplitBaseNodeData) =>
    getImageSplit(node.nbrOfOutputs, node.hasFlow),
);

export default SplitTemplate;
